import { Text, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchTelemetry } from '../../api/devices.api';
import { useT } from '../../i18n';
import { useIntlFormat } from '../../hooks/useIntlFormat';

/**
 * F5 — Ruta recorrida por el embarque.
 *
 * SVG plano con proyección lineal, sin librería de mapas: para ver la forma de
 * una ruta y dónde va el contenedor no hace falta tiles, y meter una dependencia
 * de mapas aquí obligaría a resolver API keys y CSP en el portal inversionista.
 * Si algún día se necesita el mapa base, este componente es el único que cambia.
 */

const useStyles = makeStyles({
  root:  { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  title: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  stat:  { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  empty: { padding: '14px 16px', border: `1px dashed ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  svg:   { width: '100%', height: '260px', backgroundColor: tokens.colorNeutralBackground3, borderRadius: tokens.borderRadiusMedium },
});

const VIEW_W = 600;
const VIEW_H = 260;
const PAD = 20;

export function ShipmentRouteMap({ shipmentId }: { shipmentId: string }) {
  const s = useStyles();
  const t = useT();
  const { fmtDateTime } = useIntlFormat();

  const { data: readings, isLoading, isError } = useQuery({
    queryKey: ['telemetry', 'SHIPMENT', shipmentId],
    queryFn: () => fetchTelemetry('SHIPMENT', shipmentId),
  });

  if (isLoading) return <Spinner size="tiny" />;
  if (isError) return <div className={s.empty}>{t.investorLogistics.telemetryError}</div>;

  const points = (readings ?? []).filter(
    r => typeof r.latitude === 'number' && typeof r.longitude === 'number',
  );

  if (points.length < 2) return <div className={s.empty}>{t.investorLogistics.noTelemetry}</div>;

  const lats = points.map(p => p.latitude!);
  const lons = points.map(p => p.longitude!);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLon = Math.min(...lons), maxLon = Math.max(...lons);

  // Span mínimo para que una ruta casi recta no se degenere en una división por
  // cero y termine dibujando todos los puntos encimados en una esquina.
  const spanLat = Math.max(maxLat - minLat, 1e-6);
  const spanLon = Math.max(maxLon - minLon, 1e-6);

  const x = (lon: number) => PAD + ((lon - minLon) / spanLon) * (VIEW_W - 2 * PAD);
  // La latitud crece hacia el norte y el eje Y del SVG hacia abajo: se invierte.
  const y = (lat: number) => VIEW_H - PAD - ((lat - minLat) / spanLat) * (VIEW_H - 2 * PAD);

  const path = points.map(p => `${x(p.longitude!)},${y(p.latitude!)}`).join(' ');
  const last = points[points.length - 1];

  return (
    <div className={s.root}>
      <Text className={s.title}>{t.investorLogistics.routeTitle}</Text>

      <svg className={s.svg} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} preserveAspectRatio="xMidYMid meet">
        <polyline
          points={path}
          fill="none"
          stroke={tokens.colorBrandStroke1}
          strokeWidth={2}
          strokeLinejoin="round"
        />
        <circle
          cx={x(last.longitude!)}
          cy={y(last.latitude!)}
          r={6}
          fill={tokens.colorPaletteGreenForeground1}
        />
      </svg>

      <Text className={s.stat}>
        {t.investorLogistics.currentPosition}: {last.latitude!.toFixed(4)}, {last.longitude!.toFixed(4)}
        {' · '}
        {t.investorLogistics.lastReading}: {fmtDateTime(last.recordedAt)}
      </Text>
    </div>
  );
}
